import { AlertTriangle, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { act } from "@/lib/hooks";
import { api, fdate } from "@/lib/api";
import { Section, StatusBadge, EmptyRow, StackTable } from "@/components/common";

const SUB = { active: "active", trial: "trialing", past_due: "past_due", cancelled: "canceled" };

export function DependencyWarnings({ id, warnings, reload }) {
  if (!warnings.length) return null;
  const fix = async (capability) => { if (await act(() => api.post(`/platform/tenants/${id}/resolve-dependency`, { capability }), "Prerequisite added")) reload(); };
  return (
    <div className="space-y-2" data-testid="dependency-warnings">
      {warnings.map((w) => (
        <div key={w.capability} data-testid={`dep-warning-${w.capability}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm po-tone-warning">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />{w.message}. This workspace has it enabled without the prerequisite.</span>
          <Button size="sm" variant="outline" data-testid={`dep-resolve-${w.capability}`} onClick={() => fix(w.capability)}><Wrench className="mr-1 h-3.5 w-3.5" />Add {w.missing.join(" + ")}</Button>
        </div>))}
    </div>
  );
}

export function HealthSnapshot({ h }) {
  const n = h.notifications || {};
  const cards = [["Customers", h.customers, "customers"], ["Active jobs", h.active_jobs, "active-jobs"], ["Open invoices", h.open_invoices, "open-invoices"],
    ["Outstanding bills", h.outstanding_bills, "outstanding-bills"], ["Users", h.users, "users"],
    ["Portal links (30d)", `${h.portal_links_30d?.generated ?? 0} sent · ${h.portal_links_30d?.accessed ?? 0} opened · ${h.portal_links_30d?.actioned ?? 0} actioned`, "portal-links"],
    ["Report runs (30d)", h.report_runs_30d ?? 0, "report-runs"],
    ["Email provider", n.email_provider === "custom" ? "Custom (Resend)" : "Platform default", "email-provider"],
    ["SMS", n.sms_enabled ? "Enabled" : "Disabled", "sms-enabled"],
    ["Emails sent (30d)", n.emails_sent_30d ?? 0, "emails-30d"], ["SMS sent (30d)", n.sms_sent_30d ?? 0, "sms-30d"],
    ["Failed deliveries (30d)", (n.emails_failed_30d ?? 0) + (n.sms_failed_30d ?? 0), "failed-30d"],
    ["Quotes emailed (30d)", n.quotes_sent_30d ?? 0, "quotes-sent-30d"], ["Invoices emailed (30d)", n.invoices_sent_30d ?? 0, "invoices-sent-30d"],
    ["POs emailed (30d)", n.pos_sent_30d ?? 0, "pos-sent-30d"]];
  return (
    <Section title="Health snapshot (read-only)" testId="tenant-health">
      <div className="grid gap-3 p-5 sm:grid-cols-3 lg:grid-cols-4">
        {cards.map(([l, v, k]) => <div key={k} className="po-card p-4"><div className="po-label">{l}</div><div className="mt-1 text-xl font-semibold tabular-nums" data-testid={`health-${k}`}>{v}</div></div>)}
      </div>
    </Section>
  );
}

export function FailedDeliveries({ rows }) {
  return (
    <Section title="Failed notification deliveries" testId="tenant-notification-failures">
      <div className="max-h-72 overflow-auto"><StackTable><thead><tr><th>When</th><th>Channel</th><th>Type</th><th>Reference</th><th>To</th><th className="po-num">Attempts</th><th>Error</th></tr></thead>
        <tbody>{rows.length === 0 && <EmptyRow cols={7} text="No failed deliveries." />}{rows.map((r) => (
          <tr key={r.id} data-testid={`failure-${r.id}`}><td className="whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td><td className="uppercase text-xs">{r.channel}</td><td>{r.kind.replace(/_/g, " ")}</td><td className="text-xs">{r.ref || "—"}</td>
            <td className="text-xs">{r.to}</td><td className="po-num">{r.attempts}</td><td className="max-w-[320px] truncate text-xs text-rose-700" title={r.error}>{r.error}</td></tr>))}</tbody></StackTable></div>
    </Section>
  );
}

export function ModulesAndBranches({ modules, branches }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section title="Active capabilities" testId="tenant-modules">
        <div className="flex flex-wrap gap-2 p-5">{modules.map((m) => (
          <span key={m.code} data-testid={`module-${m.code}`} className={`rounded-full border px-3 py-1 text-xs ${m.enabled ? "border-[#13294B] bg-[#13294B] text-white" : "border-slate-200 text-slate-400"}`}>
            {m.name}{m.enabled ? "" : " · off"}{m.available === false ? " · Coming soon" : ""}</span>))}</div>
      </Section>
      <Section title={`Branches (${branches.length})`} testId="tenant-branches">
        <div className="overflow-x-auto"><table className="po-table"><thead><tr><th>Branch</th><th>Code</th><th>Status</th></tr></thead>
          <tbody>{branches.length === 0 && <EmptyRow cols={3} />}{branches.map((b) => <tr key={b.id} data-testid={`branch-${b.id}`}><td className="font-medium">{b.name}{b.is_main && <span className="ml-2 text-xs text-[#389F95]">Main</span>}</td><td className="font-mono text-xs">{b.code || "—"}</td><td><StatusBadge status={b.status || "active"} /></td></tr>)}</tbody></table></div>
      </Section>
    </div>
  );
}

export function PaystackSummary({ b }) {
  const rows = [["Subscription status", <StatusBadge key="s" status={SUB[b.status] || b.status} label={(b.status || "No active subscription").replace("_", " ")} testId="billing-sub-status" />],
    ["Paystack subscription", b.has_paystack ? <span key="c" className="font-mono text-xs" data-testid="billing-sub-code">{b.subscription_code || "—"}</span> : "—"],
    ["Most recent renewal", fdate(b.last_renewal)], ["Next renewal", b.next_renewal ? fdate(b.next_renewal) : "—"]];
  return (
    <Section title="Paystack subscription" testId="tenant-paystack">
      <div className="space-y-3 p-5 text-sm">
        {!b.has_paystack && <div className="rounded-lg border px-3 py-2 po-tone-neutral" data-testid="billing-no-paystack">No Paystack subscription — billed manually ({b.manual_reason}).</div>}
        <dl className="space-y-2">{rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="font-medium">{v}</dd></div>)}</dl>
      </div>
    </Section>
  );
}
